import { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BUILT_IN_ROLE_META } from '../utils/roleCatalog';

// Hardcoded fallback roles for reliability: one entry per built-in role (ROLES in
// utils/rolePermissions.js), so the role pickers offer every role even with no roles doc.
const fallbackRoles = Object.fromEntries(
    Object.entries(BUILT_IN_ROLE_META).map(([roleId, { displayName, description, color, isSystemRole }]) => (
        [roleId, { roleId, displayName, description, color, isSystemRole }]
    ))
);

/**
 * Custom hook for managing roles
 * Provides dynamic role loading, caching, and role operations
 */
export const useRoles = () => {
    const [roles, setRoles] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const functions = getFunctions();

    // Load roles from database
    const loadRoles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const getRolesFunction = httpsCallable(functions, 'getRoles');
            const result = await getRolesFunction();
            
            if (result.data?.success && result.data?.roles) {
                // Deep merge dynamic roles with fallback roles
                // Fallback roles provide defaults, Firestore roles override
                const mergedRoles = { ...fallbackRoles };
                
                // Merge each Firestore role with its fallback (if exists)
                Object.entries(result.data.roles).forEach(([key, firestoreRole]) => {
                    mergedRoles[key] = {
                        ...(fallbackRoles[key] || {}), // Fallback properties first
                        ...firestoreRole, // Firestore properties override
                        roleId: firestoreRole.roleId || key // Ensure roleId is always set
                    };
                });
                
                setRoles(mergedRoles);
            } else {
                // Use fallback roles if API fails
                console.warn('Failed to load dynamic roles, using fallback');
                setRoles(fallbackRoles);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
            setError(error.message);
            // Use fallback roles on error
            setRoles(fallbackRoles);
        } finally {
            setLoading(false);
        }
    }, [functions]);

    // Assign role to user
    const assignRole = useCallback(async (userId, newRole, reason = '') => {
        try {
            const assignUserRoleFunction = httpsCallable(functions, 'assignUserRole');
            const result = await assignUserRoleFunction({
                userId,
                newRole,
                reason
            });
            
            if (result.data?.success) {
                return { success: true, data: result.data };
            } else {
                throw new Error(result.data?.message || 'Failed to assign role');
            }
        } catch (error) {
            console.error('Error assigning role:', error);
            throw error;
        }
    }, [functions]);

    // Get role display name
    const getRoleDisplayName = useCallback((roleId) => {
        if (!roleId || !roles[roleId]) {
            return roleId || 'Unknown Role';
        }
        return roles[roleId].displayName || roleId;
    }, [roles]);

    // Get role color
    const getRoleColor = useCallback((roleId) => {
        if (!roleId || !roles[roleId]) {
            return '#757575'; // Default gray
        }
        return roles[roleId].color || '#757575';
    }, [roles]);

    // Get role description
    const getRoleDescription = useCallback((roleId) => {
        if (!roleId || !roles[roleId]) {
            return '';
        }
        return roles[roleId].description || '';
    }, [roles]);

    // Check if role is a system role
    const isSystemRole = useCallback((roleId) => {
        if (!roleId || !roles[roleId]) {
            return false;
        }
        return roles[roleId].isSystemRole === true;
    }, [roles]);

    // Get roles as array for dropdowns
    const getRolesArray = useCallback(() => {
        return Object.values(roles).sort((a, b) => {
            // Sort system roles first, then by display name
            if (a.isSystemRole && !b.isSystemRole) return -1;
            if (!a.isSystemRole && b.isSystemRole) return 1;
            return a.displayName.localeCompare(b.displayName);
        });
    }, [roles]);

    // Get active roles only
    const getActiveRoles = useCallback(() => {
        return Object.values(roles)
            .filter(role => role.isActive !== false)
            .sort((a, b) => {
                if (a.isSystemRole && !b.isSystemRole) return -1;
                if (!a.isSystemRole && b.isSystemRole) return 1;
                return a.displayName.localeCompare(b.displayName);
            });
    }, [roles]);

    // Refresh roles (useful after creating/updating roles)
    const refreshRoles = useCallback(() => {
        loadRoles();
    }, [loadRoles]);

    // Load roles on mount
    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    return {
        roles,
        loading,
        error,
        assignRole,
        getRoleDisplayName,
        getRoleColor,
        getRoleDescription,
        isSystemRole,
        getRolesArray,
        getActiveRoles,
        refreshRoles,
        loadRoles
    };
};

export default useRoles;